import React, { memo, useCallback } from "react";
import ImageUploading from "react-images-uploading";
import { PurpleButton } from "../../../../../Components/Buttons/PurpleButton";
import { WhiteButton } from "../../../../../Components/Buttons/WhiteButton";
import {
  MintCameraIcon,
  DeleteIcon,
  ImageIcon,
} from "../../../../../AssetComponents/Images";

// Helper function to get image URL - supports both uploaded images (object) and IPFS URLs (string)
const getImageUrl = (image) => {
  if (!image) return null;
  // If it's a string (IPFS URL from draft), return it directly
  if (typeof image === 'string') return image;
  // If it's an object with data_url (newly uploaded), return data_url
  if (image.data_url) return image.data_url;
  return null;
};

const MintPhotosPage = memo(({
  images,
  setImages,
  dragStartIdx,
  setDragStartIdx,
  dragEndIdx,
  setDragEndIdx,
  removeElement,
  swapElements,
  onBack,
  onNext,
}) => {
  // Memoize drag handlers to prevent re-renders
  const handleDragStart = useCallback((idx) => {
    setDragStartIdx(idx);
  }, [setDragStartIdx]);

  const handleDragEnter = useCallback((idx) => {
    setDragEndIdx(idx);
  }, [setDragEndIdx]);

  const handleDragEnd = useCallback(() => {
    swapElements(images.slice(), dragStartIdx, dragEndIdx);
  }, [swapElements, images, dragStartIdx, dragEndIdx]);

  const handleRemove = useCallback((idx) => {
    removeElement(images.slice(), idx);
  }, [removeElement, images]);

  return (
    <div className="flex flex-col w-full h-full">
      {/* Main Content */}
      <div className="flex-1 flex flex-col md:flex-row gap-6 md:gap-[40px] justify-center items-start w-full px-4 md:px-8 py-4 md:py-8">
        {/* Upload Section */}
        <div>
          <div className="space-y-[10px] p-[16px] md:p-[20px] bg-white border-[2px] border-[#E3E3E3] rounded-[10px] flex flex-col items-center">
            <div className="font-semibold text-[18px] md:text-[20px] text-center">
              Add some photos of your Cycladic Home
            </div>
            <div className="text-[#595959] w-full max-w-[280px] md:w-[400px] text-center text-sm md:text-base">
              You'll need 5 photos to get started. You can add more or make
              changes later.
            </div>
            <ImageUploading
              multiple
              value={images}
              onChange={(images) => setImages(images)}
              maxNumber={69}
              dataURLKey="data_url"
              acceptType={[
                "jpg",
                "jpeg",
                "png",
                "gif",
                "bmp",
                "tiff",
                "tif",
                "svg",
                "webp",
                "heic",
                "heif",
              ]}
            >
              {({
                imageList,
                onImageUpload,
                onImageRemove,
                isDragging,
                dragProps,
              }) => (
                <div
                  className={
                    isDragging
                      ? "border-[2px] rounded-[12px] w-full max-w-[280px] md:w-[400px] h-[200px] md:h-[300px] justify-center space-y-[8px] md:space-y-[10px] flex flex-col items-center bg-[#E3E3E3]"
                      : "border-[2px] rounded-[12px] w-full max-w-[280px] md:w-[400px] h-[200px] md:h-[300px] justify-center space-y-[8px] md:space-y-[10px] flex flex-col items-center bg-[#F6F6F6]"
                  }
                  {...dragProps}
                >
                  <MintCameraIcon />
                  <PurpleButton onClick={onImageUpload} text="Upload file" />
                  <div className="text-[#595959] text-sm md:text-base">
                    ... or drag and drop files
                  </div>
                </div>
              )}
            </ImageUploading>
          </div>
        </div>
        
        {/* Photo Preview Section */}
        <div className="w-full md:w-[400px]">
          <div className="max-h-[50vh] md:max-h-[70vh] overflow-auto">
            <div className="text-[20px] md:text-[24px] font-semibold mb-2">
              Choose at least 5 photos
            </div>
            <div className="mb-[10px] text-sm md:text-base">Drag to reorder</div>
            
            {/* Cover Photo */}
            {images[0] ? (
              <div className="relative group mb-3">
                <div className="absolute bg-white px-[8px] md:px-[10px] text-[12px] md:text-[14px] font-normal py-[4px] md:py-[6px] top-[8px] md:top-[10px] left-[8px] md:left-[10px] rounded-[6px] md:rounded-[8px]">
                  Cover photo
                </div>
                <div
                  onClick={() => handleRemove(0)}
                  className="group-hover:block hidden shadow-md cursor-pointer absolute bg-white p-[6px] md:p-[8px] bottom-[8px] md:bottom-[10px] right-[8px] md:right-[10px] rounded-full"
                >
                  <DeleteIcon className="w-[14px] md:w-[16px]" />
                </div>
                <img
                  alt=""
                  src={getImageUrl(images[0])}
                  className="w-full rounded-[8px] md:rounded-[10px]"
                  onDragStart={() => handleDragStart(0)}
                  onDragEnter={() => handleDragEnter(0)}
                  onDragEnd={handleDragEnd}
                />
              </div>
            ) : (
              <div className="flex justify-center items-center w-full h-full min-h-[150px] md:min-h-[200px] border-[2px] border-dashed rounded-[8px] mb-3">
                <ImageIcon />
              </div>
            )}

            {/* Grid of Additional Photos */}
            <div className="grid grid-cols-2 gap-[8px] md:gap-[10px] mt-[10px]">
              {[1, 2, 3, 4].map((idx) => {
                if (images[idx])
                  return (
                    <div key={idx} className="relative group">
                      <div
                        onClick={() => handleRemove(idx)}
                        className="hidden group-hover:block group shadow-md cursor-pointer absolute bg-white p-[6px] md:p-[8px] bottom-[8px] md:bottom-[10px] right-[8px] md:right-[10px] rounded-full"
                      >
                        <DeleteIcon className="w-[14px] md:w-[16px]" />
                      </div>
                      <img
                        alt=""
                        draggable
                        src={getImageUrl(images[idx])}
                        className="rounded-[6px] md:rounded-[8px] w-full h-[100px] md:h-full min-h-[140px] object-cover"
                        onDragStart={() => handleDragStart(idx)}
                        onDragEnter={() => handleDragEnter(idx)}
                        onDragEnd={handleDragEnd}
                      />
                    </div>
                  );
                else
                  return (
                    <div key={idx} className="flex justify-center items-center w-full h-[100px] md:h-full min-h-[140px] border-[2px] border-dashed rounded-[6px] md:rounded-[8px]">
                      <ImageIcon />
                    </div>
                  );
              })}

              {/* Additional photos beyond the first 5 */}
              {images.slice(5).map((img, idx) => {
                const actualIdx = idx + 5;
                return (
                  <div key={actualIdx} className="relative group">
                    <div
                      onClick={() => handleRemove(actualIdx)}
                      className="hidden group-hover:block group shadow-md cursor-pointer absolute bg-white p-[6px] md:p-[8px] bottom-[8px] md:bottom-[10px] right-[8px] md:right-[10px] rounded-full"
                    >
                      <DeleteIcon className="w-[14px] md:w-[16px]" />
                    </div>
                    <img
                      alt=""
                      draggable
                      src={getImageUrl(images[actualIdx])}
                      className="rounded-[6px] md:rounded-[8px] w-full h-[100px] md:h-full min-h-[140px] object-cover"
                      onDragStart={() => handleDragStart(actualIdx)}
                      onDragEnter={() => handleDragEnter(actualIdx)}
                      onDragEnd={handleDragEnd}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Buttons - Fixed at Bottom */}
      <div className="w-full flex flex-row gap-3 md:justify-between items-center px-4 md:px-8 py-3 md:py-6 bg-white border-t border-gray-200 shadow-lg">
        <WhiteButton  
          text="Back"
          onClick={onBack} 
          className="flex-1 md:w-[120px] h-[45px] md:h-[50px] flex items-center justify-center text-sm md:text-base font-medium"
        />
        <PurpleButton
          text="Next"
          onClick={onNext}
          className="flex-1 md:w-[140px] h-[45px] md:h-[50px] flex items-center justify-center text-sm md:text-base font-medium"
        />
      </div>
    </div>
  );
});

MintPhotosPage.displayName = "MintPhotosPage";

export default MintPhotosPage;
